/**
 * HtmlEmbed — renders CMS-authored raw HTML ("Block Grid - HTML Embed") and,
 * unlike dangerouslySetInnerHTML, EXECUTES any <script> tags it contains by
 * re-creating them (innerHTML-inserted scripts never run). Client-only.
 *
 * Content comes from Umbraco editors — the same trust level as the razor
 * blockGridHtml partial that renders this block verbatim on other pages.
 */

import React, { useEffect, useRef } from 'react';

interface HtmlEmbedProps {
  html: string;
}

const HtmlEmbed: React.FC<HtmlEmbedProps> = ({ html }) => {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof window === 'undefined') return;

    host.innerHTML = '';
    const template = document.createElement('template');
    template.innerHTML = html;

    // Re-create script elements so the browser executes them. Src scripts
    // are deduped page-wide (a widget script only needs loading once).
    template.content.querySelectorAll('script').forEach((original) => {
      const src = original.getAttribute('src');
      if (src && document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
        original.remove();
        return;
      }
      const script = document.createElement('script');
      Array.from(original.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.text = original.text;
      original.replaceWith(script);
    });

    host.appendChild(template.content);

    // Convenience auto-loaders so editors paste ONLY the widget markup —
    // no script tags with URLs that go stale between releases.

    // 1. Canonical recs widget: <div data-subaru-recs …> populated by the
    //    stable CDN loader (see the recs-widget doc on the artifacts site).
    if (host.querySelector('#subaru-recs,[data-subaru-recs]')) {
      const w = window as any;
      const loaderSrc = 'https://cdn.inchcape-web.au/widgets/recs.js';
      if (w.SubaruRecsWidget) {
        // already loaded (its self-boot ran before this node existed):
        // rescan so the new node renders
        w.SubaruRecsWidget.boot();
      } else if (!document.querySelector(`script[src="${loaderSrc}"]`)) {
        const script = document.createElement('script');
        script.defer = true;
        script.src = loaderSrc;
        document.head.appendChild(script);
        // recs.js self-boots on execution (readyState guard), which happens
        // after this node is in the DOM — no callback needed.
      }
    }

    // 2. <cdp-recs> web component: load its bundle from the SAME release
    //    path as the page's own bundles.
    if (
      host.querySelector('cdp-recs') &&
      !window.customElements.get('cdp-recs')
    ) {
      const pageBundle = document.querySelector<HTMLScriptElement>(
        'script[src*="/dist/js/"]'
      );
      const src = pageBundle
        ?.getAttribute('src')
        ?.replace(/\/js\/[^/]+$/, '/js/cdp-recs.bundle.js');
      if (src && !document.querySelector(`script[src="${CSS.escape(src)}"]`)) {
        const script = document.createElement('script');
        script.defer = true;
        script.src = src;
        document.head.appendChild(script);
      }
    }
  }, [html]);

  return <div ref={hostRef} data-testid="html-embed" />;
};

export default HtmlEmbed;
